import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { SocialShareBar } from "../components/SocialShareBar";
import { ShareToCommunityButton } from "../components/community/ShareToCommunityButton";
import { PolishVideoButton } from "../components/PolishVideoButton";
import { AssistantChat } from "../components/AssistantChat";
import VideoPlayerDialog from "../components/VideoPlayerDialog";
import { iosSave } from "../lib/iosSave";
import { useBrainDefaults } from "../hooks/useBrainDefaults";
import { errMsg } from "../lib/errMsg";
import { 
  Video, 
  Sparkles, 
  Wand2, 
  Play,
  Download,
  Clock,
  Loader2,
  Lock,
  Crown,
  Music,
  Shuffle,
  PenTool,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ChevronRight,
  Package,
  Upload,
  ImageIcon,
  X,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIVideoGeneratorPage = () => {
  const { token, user } = useAuth();

  // Surprise Me is gated behind XEA Brain learning — the page asks the
  // brain how many Marketing events the user has shipped, and only
  // unlocks the "Surprise Me" mode once that count crosses the
  // threshold. Until then, the card shows a friendly "locked" state
  // with a 0/N progress meter. Once unlocked, the brain's learned
  // preferences power the surprise picks.
  const SURPRISE_ME_UNLOCK_AT = 3;
  const [brainMarketingEvents, setBrainMarketingEvents] = useState(null);   // null = loading
  const surpriseMeUnlocked = (brainMarketingEvents ?? 0) >= SURPRISE_ME_UNLOCK_AT;
  const SURPRISE_ME_ENABLED = surpriseMeUnlocked;

  useEffect(() => {
    if (!token) return;
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/brain/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancel) return;
        const n = ((r.data?.per_realm || {}).marketing || {}).total_events || 0;
        setBrainMarketingEvents(n);
      } catch (_e) {
        if (!cancel) setBrainMarketingEvents(0);
      }
    })();
    return () => { cancel = true; };
  }, [token]);

  const [mode, setMode] = useState("custom"); // default Custom until the brain unlocks Surprise Me
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [videoHistory, setVideoHistory] = useState([]);
  const [pollingTasks, setPollingTasks] = useState({});
  // Per-task ETA from backend (cached so we don't re-fetch when the 1Hz tick runs).
  // Shape: { [taskId]: { eta_seconds, clip_count } }
  const [taskEta, setTaskEta] = useState({});
  // 1Hz tick so the "elapsed" counter on processing cards re-renders smoothly
  // without waiting for the 5-second poll cycle.
  const [nowTick, setNowTick] = useState(() => Date.now());
  
  // Custom mode state
  const [customPrompt, setCustomPrompt] = useState("");
  const [videoStyle, setVideoStyle] = useState("trendy");
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [duration, setDuration] = useState("15");
  // Pass 4 — duration is auto by default. The user can still override via a
  // tiny "Customize" pill that opens a 15-60s number input.
  const [customizeDuration, setCustomizeDuration] = useState(false);
  // When the AI chat returns a "📹 VIDEO PROMPT · Recommended: Xs" block,
  // the user clicking "Use as video prompt" pre-selects that duration and
  // locks the other two options. Set to null to free the duration picker.
  const [aiSuggestedDuration, setAiSuggestedDuration] = useState(null);
  const [musicPreference, setMusicPreference] = useState("");
  // Music picker — `auto` lets the backend match the vibe to the chosen style.
  // `none` explicitly opts out of any music polish. Library keys come from
  // GET /api/video-gen/music-library.
  const [musicChoice, setMusicChoice] = useState("auto");
  // Pass 4 polish-mode tri-state: "music" (full polish + auto-routed song),
  // "brand_only" (brand frame + CTA overlay, NO music — Kling's native audio
  // is preserved), "raw" (zero polish — just the stitched Kling clip).
  const [polishMode, setPolishMode] = useState("music");
  // Free-text genre/vibe the user types after the AssistantChat asks. Sent
  // alongside polish_mode='music' so the backend can route the best engine.
  const [musicGenre, setMusicGenre] = useState("");
  const [musicLibrary, setMusicLibrary] = useState([]);
  const [customMusic, setCustomMusic] = useState(null);     // {music_url, filename, size_bytes}
  const [uploadingMusic, setUploadingMusic] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [aiMusic, setAiMusic] = useState(null);             // {music_url, vibe, duration_seconds}
  // Jamendo (real human-made songs) — open to every tier.
  const [jamendoOpen, setJamendoOpen] = useState(false);
  const [jamendoQuery, setJamendoQuery] = useState("");
  const [jamendoMood, setJamendoMood] = useState("");
  const [jamendoLoading, setJamendoLoading] = useState(false);
  const [jamendoTracks, setJamendoTracks] = useState([]);
  const [jamendoSelecting, setJamendoSelecting] = useState(null); // track_id being mirrored
  const [jamendoChosen, setJamendoChosen] = useState(null); // {music_url, name, artist_name, license_url}
  // ElevenLabs Music (AI vocal songs) — Pro / Platinum / Ultimate only.
  const [elevenOpen, setElevenOpen] = useState(false);
  const [elevenPrompt, setElevenPrompt] = useState("");
  const [elevenDuration, setElevenDuration] = useState(30);
  const [elevenInstrumental, setElevenInstrumental] = useState(false);
  // Pass 5 — optional lyrics for ElevenLabs vocals. When non-empty and the
  // "Instrumental only" toggle is OFF, these get embedded in the prompt with
  // [Section] markers so the model sings actual words.
  const [elevenLyrics, setElevenLyrics] = useState("");
  const [elevenGenerating, setElevenGenerating] = useState(false);
  const [elevenChosen, setElevenChosen] = useState(null); // {music_url, prompt, duration}
  // Unified "Search or generate" dialog state — collapses the previous
  // 3 separate music tiles into one entry point per founder feedback.
  const [smartMusicOpen, setSmartMusicOpen] = useState(false);
  const previewAudioRef = useRef(null);
  const [previewingKey, setPreviewingKey] = useState(null);
  const [enableAudio, setEnableAudio] = useState(true);
  const [klingModel, setKlingModel] = useState("kling-v2-6");

  // ── Pass 3 power features ─────────────────────────────────────────────
  // Negative prompt — what to AVOID in the video. AssistantChat auto-fills
  // this from the "🚫 *Negative*: ..." line it emits with every video prompt.
  const [negativePrompt, setNegativePrompt] = useState("");
  // Multi-shot mode — when ON and duration is 20s or 30s (2 clips), the user
  // writes a distinct prompt for each shot. Falls back to single prompt otherwise.
  const [multiShot, setMultiShot] = useState(false);
  const [shotPrompts, setShotPrompts] = useState(["", ""]);
  // SFX overlay — short ElevenLabs sound effect (whoosh / impact / sparkle…)
  // mixed at clip 0:00. Pro+ tier only.
  const [sfxPrompt, setSfxPrompt] = useState("");
  const [sfxDuration, setSfxDuration] = useState(2);
  const [sfxChosen, setSfxChosen] = useState(null); // {sfx_url, prompt, duration}
  const [generatingSfx, setGeneratingSfx] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Chat-assisted Custom flow — uploaded reference image used as the source
  // image for Kling AND fed to DeepSeek (via vision description) so it can
  // give specific opinions.
  const [referenceImage, setReferenceImage] = useState(null); // {image_url, description, filename, size_bytes}
  const [uploadingRef, setUploadingRef] = useState(false);
  
  // Editing state
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editPrompt, setEditPrompt] = useState("");
  const [editDuration, setEditDuration] = useState("15");

  // In-app video player dialog state — replaces the old "Play opens new tab" flow.
  const [playerVideo, setPlayerVideo] = useState(null);    // { url, title } | null

  // XEA Brain — smart defaults for Marketing realm. Applied once on first
  // mount, only when the user hasn't manually touched the field yet.
  const { defaults: brainDefaults } = useBrainDefaults("marketing");
  const [brainApplied, setBrainApplied] = useState(false);

  useEffect(() => {
    if (brainApplied) return;
    if (!brainDefaults || Object.keys(brainDefaults).length === 0) return;
    const applied = [];
    if (brainDefaults.style && videoStyle === "trendy") {
      setVideoStyle(brainDefaults.style); applied.push(`style → ${brainDefaults.style}`);
    }
    if (brainDefaults.aspect_ratio && aspectRatio === "9:16") {
      setAspectRatio(brainDefaults.aspect_ratio); applied.push(`aspect → ${brainDefaults.aspect_ratio}`);
    }
    if (brainDefaults.polish_mode && polishMode === "music" && brainDefaults.polish_mode !== "music") {
      setPolishMode(brainDefaults.polish_mode); applied.push(`polish → ${brainDefaults.polish_mode}`);
    }
    if (brainDefaults.music_genre && !musicGenre) {
      setMusicGenre(brainDefaults.music_genre); applied.push(`music vibe → ${brainDefaults.music_genre}`);
    }
    if (brainDefaults.kling_model && klingModel === "kling-v2-6" && brainDefaults.kling_model !== "kling-v2-6") {
      setKlingModel(brainDefaults.kling_model); applied.push(`model → ${brainDefaults.kling_model}`);
    }
    if (applied.length > 0) {
      toast.success(`🧠 XEA pre-filled your usual picks: ${applied.slice(0, 2).join(", ")}${applied.length > 2 ? "…" : ""}`, { duration: 4000 });
    }
    setBrainApplied(true);
  }, [brainDefaults]);

  useEffect(() => {
    fetchProducts();
    fetchVideoHistory();
    // Music library is public — fetched once per session.
    axios.get(`${API_URL}/api/video-gen/music-library`).then((r) => {
      setMusicLibrary(r.data?.tracks || []);
    }).catch(() => { /* non-fatal — picker falls back to a static list */ });
  }, [token]);

  // Poll for video status updates
  useEffect(() => {
    const pollInterval = setInterval(() => {
      Object.keys(pollingTasks).forEach(async (taskId) => {
        try {
          const response = await axios.get(
            `${API_URL}/api/video-gen/status/${taskId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );

          const data = response.data;
          // Cache the backend-computed ETA + clip count for the live timer below.
          if (data.eta_seconds || data.clip_count) {
            setTaskEta((prev) => ({
              ...prev,
              [taskId]: { eta_seconds: data.eta_seconds, clip_count: data.clip_count },
            }));
          }
          // All terminal states from the backend pipeline. Missing any of
          // these would leave the UI spinning forever — exactly the bug we
          // had with `timeout` / `error` going unrecognized.
          const TERMINAL_FAIL = new Set(["failed", "timeout", "error", "cancelled"]);
          const isFailed = TERMINAL_FAIL.has(data.status);

          // Custom / Text-to-Video / Edit also auto-polish now (music bed),
          // not just Surprise Me. Wait for polish to finish across the board
          // so the user sees the polished version, not the raw Kling clip.
          // ── Pass 4 race-fix ──────────────────────────────────────────
          // `polish_mode==='raw'` explicitly opts out. Otherwise we EXPECT
          // polish to run (music or brand_only). The previous logic treated
          // "completed but polish_status null" as DONE — which raced against
          // the backend's `update_one(status=completed)` and the subsequent
          // `_auto_polish_if_surprise_me` write of polish_status="processing".
          // The frontend would land in that ~50ms gap, declare done, and
          // show the user the RAW clip without music (perfume bug).
          //
          // Fix: when we expect polish AND polish hasn't started yet, keep
          // polling for up to GRACE_MS after status hit "completed". After
          // that grace window we assume polish was skipped/silently dropped
          // and stop polling.
          const polishOptIn = (data.polish_mode || "music") !== "raw" &&
                              (data.music_choice && data.music_choice !== "none");
          const willAutoPolish = polishOptIn;
          const polishStarted = !!data.polish_status;
          const polishDone = data.polish_status === "completed" || data.polish_status === "failed";

          // Track when status FIRST flipped to "completed" so we can apply a
          // grace window for polish setup. Stored alongside the polling task.
          const POLISH_GRACE_MS = 30_000;   // 30s grace for polish to register
          const completedAt = data.completed_at ? new Date(data.completed_at).getTime() : null;
          const sinceCompleted = completedAt ? (Date.now() - completedAt) : Infinity;
          const exceededGrace = sinceCompleted > POLISH_GRACE_MS;

          const fullyDone =
            isFailed ||
            (data.status === "completed" &&
              (
                !willAutoPolish ||              // opted out → done immediately
                polishDone ||                   // polish finished (success or fail)
                (!polishStarted && exceededGrace)  // polish never started after grace → give up
              ));

          if (fullyDone) {
            setPollingTasks(prev => {
              const updated = { ...prev };
              delete updated[taskId];
              return updated;
            });
            fetchVideoHistory();

            if (data.status === "completed") {
              if (willAutoPolish && data.polish_status === "failed") {
                toast.success("Video ready! Music polish step skipped — raw clip is downloadable.");
              } else {
                toast.success("Video generation complete!");
              }
            } else if (data.status === "timeout") {
              toast.error("Video generation timed out. Kling AI is slow right now — please retry, or try a shorter duration.");
            } else if (data.status === "error") {
              toast.error(data.error || "Video generation hit an unexpected error. Please retry.");
            } else {
              toast.error(data.error || "Video generation failed");
            }
          } else if (data.status === "completed" && willAutoPolish && data.polish_status === "processing") {
            // Show interim progress so the user sees something happening
            fetchVideoHistory();
          }
        } catch (error) {
          console.error("Polling error:", error);
        }
      });
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [pollingTasks, token]);

  // ── Smooth 1-second tick for the "elapsed" counter on processing cards ──
  // Only runs while at least one task is processing so we don't waste cycles.
  useEffect(() => {
    if (Object.keys(pollingTasks).length === 0) return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [pollingTasks]);

  // Format mm:ss / hh:mm:ss
  const fmtElapsed = (secs) => {
    if (!Number.isFinite(secs) || secs < 0) return "0:00";
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = Math.floor(secs % 60);
    return h > 0
      ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`
      : `${m}:${String(s).padStart(2, "0")}`;
  };
  // Build the live processing message: "3:42 elapsed · ~12 min total · 2 clips rendering"
  const processingMsg = (video) => {
    if (!video?.created_at) return null;
    const elapsedSec = Math.max(0, Math.floor((nowTick - new Date(video.created_at).getTime()) / 1000));
    const meta = taskEta[video.id] || {};
    const etaMin = meta.eta_seconds ? Math.round(meta.eta_seconds / 60) : null;
    const clipCount = meta.clip_count || 1;
    const parts = [`${fmtElapsed(elapsedSec)} elapsed`];
    if (etaMin) parts.push(`~${etaMin} min total`);
    if (clipCount > 1) parts.push(`${clipCount} clips rendering in parallel`);
    // Stuck-warning when elapsed > 1.5× ETA: gently nudge the user to retry.
    if (meta.eta_seconds && elapsedSec > meta.eta_seconds * 1.5) {
      parts.push("running longer than expected — Kling is busy");
    }
    return parts.join(" · ");
  };

  // Pass 4 — multi-shot is available whenever the resolved duration plans
  // into exactly 2 Kling clips. Our compute_clip_plan packs 20-30s into 2,
  // and >30s into 3+. So we gate multi-shot on 20 ≤ resolved ≤ 30.
  const resolvedDurationInt = customizeDuration
    ? parseInt(duration || "15", 10)
    : (aiSuggestedDuration ? parseInt(aiSuggestedDuration, 10) : 15);
  const multiShotAvailable = Number.isFinite(resolvedDurationInt) && resolvedDurationInt >= 20 && resolvedDurationInt <= 30;

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // API returns array directly
      setProducts(Array.isArray(response.data) ? response.data : response.data.products || []);
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const fetchVideoHistory = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/video-gen/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setVideoHistory(response.data.videos || []);
    } catch (error) {
      console.error("Error fetching video history:", error);
    }
  };

  const handleSurpriseMe = async () => {
    if (!selectedProduct) {
      toast.error("Please select a product first");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/video-gen/surprise-me`,
        {
          product_id: selectedProduct.id,
          duration: parseInt(duration),
          aspect_ratio: aspectRatio,
          enable_audio: enableAudio,
          model: klingModel
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`${response.data.message}! Music suggestion: ${response.data.music_suggestion}`);
      setPollingTasks(prev => ({ ...prev, [response.data.task_id]: true }));
      fetchVideoHistory();
    } catch (error) {
      const message = errMsg(error, "Failed to generate video");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCustomGenerate = async () => {
    // Two valid sources now: a selected product OR an uploaded reference image
    if (!selectedProduct && !referenceImage) {
      toast.error("Pick a product or upload a reference image first");
      return;
    }
    if (!customPrompt.trim()) {
      toast.error("Please enter a prompt");
      return;
    }

    setLoading(true);
    try {
      const body = {
        prompt: customPrompt,
        // Pass 4 — duration is auto by default. Send null when the user hasn't
        // overridden it AND the AssistantChat hasn't locked a specific value;
        // backend defaults to 15s. When override or AI lock is set, send that.
        duration: customizeDuration
          ? parseInt(duration)
          : (aiSuggestedDuration ? parseInt(aiSuggestedDuration) : null),
        aspect_ratio: aspectRatio,
        style: videoStyle,
        polish_mode:  polishMode,
        music_genre:  polishMode === "music" ? (musicGenre || null) : null,
        music_preference: musicPreference || null,
        music_choice: polishMode === "music" ? musicChoice : (polishMode === "brand_only" ? "none" : "none"),
        custom_music_url: musicChoice === "custom" ? customMusic?.music_url || null : null,
        enable_audio: enableAudio,
        model: klingModel,
        negative_prompt: negativePrompt.trim() || null,
        sfx_url: sfxChosen?.sfx_url || null,
      };
      // Multi-shot: when toggle is on and duration is 20s/30s, send per-clip
      // prompts (each non-empty, falling back to the main prompt). Backend
      // validates length against the clip plan.
      if (multiShot && multiShotAvailable) {
        const filled = shotPrompts.map((s) => (s || "").trim()).filter(Boolean);
        if (filled.length < 2) {
          toast.error("Multi-shot mode needs a prompt for each shot");
          setLoading(false);
          return;
        }
        body.shots = shotPrompts.map((s) => (s || "").trim() || customPrompt);
      }
      // Reference image takes precedence when both are present
      if (referenceImage?.image_url) {
        body.reference_image_url = referenceImage.image_url;
      } else if (selectedProduct) {
        body.product_id = selectedProduct.id;
      }

      const response = await axios.post(
        `${API_URL}/api/video-gen/custom`,
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const taskId = response.data.task_id;
      // Friendlier expectation-setter: tell the user the realistic wall-time
      // upfront so they don't think it's stuck at 5 min on a 12-min job.
      const isMulti  = !!body.shots;
      const isPro10s = klingModel === "kling-v3" || parseInt(duration) >= 10;
      const etaMin   = isMulti ? (isPro10s ? 12 : 8) : (isPro10s ? 7 : 4);
      toast.success(
        `Generating${isMulti ? " multi-shot" : ""} video — usually ready in ~${etaMin} min. Track progress in History below.`,
        { duration: 7000 },
      );
      setPollingTasks(prev => ({ ...prev, [taskId]: true }));
      setCustomPrompt("");
      setReferenceImage(null);
      setNegativePrompt("");
      setShotPrompts(["", ""]);
      setSfxChosen(null);
      setSfxPrompt("");
      fetchVideoHistory();
    } catch (error) {
      const message = errMsg(error, "Failed to generate video");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  // Reference image upload — runs Gemini Vision on the backend so DeepSeek
  // can "see" the picture and give opinions, and the same URL is what we
  // pass to Kling on generate.
  const handleReferenceUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image too large. Max 10MB.");
      return;
    }
    setUploadingRef(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API_URL}/api/chat/upload-reference`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setReferenceImage(r.data);
      toast.success("Reference image attached — ask XEA anything about it");
    } catch (e) {
      toast.error(errMsg(e, "Upload failed"));
    } finally {
      setUploadingRef(false);
    }
  };

  // Toggle preview playback for a library track.
  const previewTrack = (key) => {
    const audio = previewAudioRef.current;
    if (!audio) return;
    if (previewingKey === key) {
      audio.pause();
      setPreviewingKey(null);
      return;
    }
    audio.src = `${API_URL}/api/video-gen/music-preview/${key}.mp3`;
    audio.play().then(() => setPreviewingKey(key)).catch(() => setPreviewingKey(null));
  };

  // Upload a custom MP3/WAV — stored in object storage so it survives
  // redeploys and is reusable across generations.
  const handleMusicUpload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Audio too large. Max 10MB.");
      return;
    }
    setUploadingMusic(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(`${API_URL}/api/video-gen/upload-music`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCustomMusic(r.data);
      setMusicChoice("custom");
      toast.success(`Music attached: ${r.data.filename}`);
    } catch (e) {
      toast.error(errMsg(e, "Music upload failed"));
    } finally {
      setUploadingMusic(false);
    }
  };

  // Generate a unique AI music track via Mubert. Vibe defaults to the
  // active musicChoice when it's a library key; otherwise "trendy".
  // Duration matches the requested video length so the polish doesn't
  // tail off into silence.
  const handleGenerateAIMusic = async () => {
    const vibe = ["trendy", "chill", "luxury", "energetic"].includes(musicChoice)
      ? musicChoice
      : "trendy";
    setGeneratingAI(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/video-gen/generate-ai-music`,
        { vibe, duration: parseInt(duration) || 30, intensity: "medium" },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setAiMusic(r.data);
      // Re-use the existing custom-music plumbing — the AI track is just a URL
      setCustomMusic({
        music_url: r.data.music_url,
        filename: `AI track (${r.data.vibe}, ${r.data.duration_seconds}s)`,
        size_bytes: 0,
      });
      setMusicChoice("custom");
      toast.success("AI track generated — preview below");
    } catch (e) {
      toast.error(errMsg(e, "AI music generation failed"));
    } finally {
      setGeneratingAI(false);
    }
  };

  // Jamendo — search the catalog (debounced on user submit, not keystroke).
  const handleJamendoSearch = async () => {
    setJamendoLoading(true);
    try {
      const params = new URLSearchParams();
      if (jamendoQuery.trim()) params.set("q", jamendoQuery.trim());
      if (jamendoMood) params.set("mood", jamendoMood);
      params.set("limit", "24");
      const r = await axios.get(`${API_URL}/api/music/jamendo/search?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJamendoTracks(r.data?.tracks || []);
      if ((r.data?.tracks || []).length === 0) {
        toast.message("No tracks matched — try a different mood or keyword.");
      }
    } catch (e) {
      toast.error(errMsg(e, "Jamendo search failed"));
    } finally {
      setJamendoLoading(false);
    }
  };

  // Jamendo — pick a track, backend mirrors MP3 to object storage.
  const handleJamendoSelect = async (track) => {
    setJamendoSelecting(track.id);
    try {
      const r = await axios.post(
        `${API_URL}/api/music/jamendo/select`,
        { track_id: track.id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setJamendoChosen(r.data);
      setCustomMusic({
        music_url: r.data.music_url,
        filename: `${r.data.name} — ${r.data.artist_name}`,
        size_bytes: 0,
      });
      setMusicChoice("custom");
      setAiMusic(null);
      setElevenChosen(null);
      toast.success(`Track attached: ${r.data.name} by ${r.data.artist_name}`);
      setJamendoOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "Failed to attach track"));
    } finally {
      setJamendoSelecting(null);
    }
  };

  // ElevenLabs Music — generate a unique vocal song from a prompt.
  const [vibeRef, setVibeRef] = useState("");
  const [vibeTranslating, setVibeTranslating] = useState(false);

  const handleTranslateVibe = async () => {
    const ref = vibeRef.trim();
    if (ref.length < 2) {
      toast.error("Type a song name or artist first.");
      return;
    }
    setVibeTranslating(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/music/describe-vibe`,
        { song_reference: ref },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setElevenPrompt(r.data.vibe);
      toast.success("Vibe captured — tweak it if you like, then generate.");
    } catch (e) {
      toast.error(errMsg(e, "Vibe translation failed"));
    } finally {
      setVibeTranslating(false);
    }
  };

  const handleElevenLabsGenerate = async () => {
    if (!elevenPrompt.trim() || elevenPrompt.trim().length < 4) {
      toast.error("Describe the song in at least a few words.");
      return;
    }
    setElevenGenerating(true);
    try {
      const r = await axios.post(
        `${API_URL}/api/music/elevenlabs/generate`,
        {
          prompt: elevenPrompt.trim(),
          duration: parseInt(elevenDuration) || 30,
          instrumental: elevenInstrumental,
          lyrics: elevenInstrumental ? null : (elevenLyrics.trim() || null),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setElevenChosen(r.data);
      setCustomMusic({
        music_url: r.data.music_url,
        filename: `AI vocal song (${r.data.duration}s)`,
        size_bytes: 0,
      });
      setMusicChoice("custom");
      setAiMusic(null);
      setJamendoChosen(null);
      toast.success("AI vocal song generated — attached to your video");
      setElevenOpen(false);
    } catch (e) {
      toast.error(errMsg(e, "AI vocal song generation failed"));
    } finally {
      setElevenGenerating(false);
    }
  };



  const handleEditVideo = async (taskId) => {
    if (!editPrompt.trim()) {
      toast.error("Please enter edit instructions");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/video-gen/edit/${taskId}`,
        {
          video_task_id: taskId,
          edit_prompt: editPrompt,
          duration: parseInt(editDuration),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(response.data.message);
      setPollingTasks(prev => ({ ...prev, [response.data.task_id]: true }));
      setEditingTaskId(null);
      setEditPrompt("");
      fetchVideoHistory();
    } catch (error) {
      const message = errMsg(error, "Failed to edit video");
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-coral-500" />;
      case "failed":
      case "error":
        return <XCircle className="w-5 h-5 text-red-400" />;
      case "processing":
        return <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-ink-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "text-coral-500";
      case "failed":
      case "error":
        return "text-red-400";
      case "processing":
        return "text-amber-400";
      default:
        return "text-ink-500";
    }
  };

  // Free users CAN generate — they just have limited tokens (25/month default).
  // The backend enforces the actual spend; here we show a friendly nudge banner
  // so they understand the value of upgrading without blocking the workflow.
  const isFree = user?.subscription_tier === "free";
  const canGenerate = true;

  // Prefer the polished (TikTok-style with music + overlays) URL when ready.
  // Surprise Me auto-polishes, so this is usually populated for that mode.
  const displayUrl = (v) => v.polished_url || v.video_url;
  const isPolishing = (v) => v.polish_status === "processing";

  return (
    <div className="space-y-8" data-testid="ai-video-generator-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-ink-950 tracking-tight flex items-center gap-3">
          <Video className="w-8 h-8 text-coral-500" />
          AI Video Generator
        </h1>
        <p className="text-ink-500 mt-1">
          Create stunning TikTok-style videos with AI-powered editing
        </p>
      </div>

      {/* Free Plan Notice — soft nudge, doesn't block the workflow */}
      {isFree && (
        <div
          data-testid="free-plan-notice"
          className="p-5 rounded-2xl bg-gradient-to-r from-violet-900/40 to-purple-900/40 border border-coral-500/30"
        >
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-coral-500/20 flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-coral-500" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-ink-950">
                  You&apos;re on the Free plan
                </h3>
                <p className="text-violet-300 text-sm">
                  {user?.tokens ?? 0} tokens remaining · ~{Math.floor((user?.tokens ?? 0) / 10)} short videos. Upgrade for more tokens, longer clips, and priority queue.
                </p>
              </div>
            </div>
            <Link to="/dashboard/subscription">
              <Button className="bg-coral-500 hover:bg-coral-600 gap-2" data-testid="upgrade-from-video-btn">
                <Crown className="w-4 h-4" />
                Upgrade
              </Button>
            </Link>
          </div>
        </div>
      )}

      {/* Mode Selector */}
      {canGenerate && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card 
            className={`transition-all ${
              !SURPRISE_ME_ENABLED
                ? "bg-ink-100 border-ink-200 cursor-not-allowed opacity-60"
                : mode === "surprise" 
                ? "cursor-pointer bg-gradient-to-br from-violet-900/50 to-purple-900/50 border-coral-500/40 ring-2 ring-violet-500/30" 
                : "cursor-pointer bg-white/60 border-ink-200 hover:border-coral-500/30"
            }`}
            onClick={() => SURPRISE_ME_ENABLED && setMode("surprise")}
            data-testid="surprise-me-mode"
            aria-disabled={!SURPRISE_ME_ENABLED}
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  !SURPRISE_ME_ENABLED ? "bg-ink-200" : mode === "surprise" ? "bg-coral-500/30" : "bg-ink-100"
                }`}>
                  <Shuffle className={`w-7 h-7 ${!SURPRISE_ME_ENABLED ? "text-ink-400" : "text-coral-500"}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-lg font-semibold text-ink-950">Surprise Me</h3>
                    {!SURPRISE_ME_ENABLED && (
                      <span
                        data-testid="surprise-me-disabled-badge"
                        className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-violet-100 text-violet-700"
                      >
                        🧠 {Math.min(brainMarketingEvents ?? 0, SURPRISE_ME_UNLOCK_AT)}/{SURPRISE_ME_UNLOCK_AT} to unlock
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-ink-500">
                    {!SURPRISE_ME_ENABLED
                      ? `Ship ${SURPRISE_ME_UNLOCK_AT - (brainMarketingEvents ?? 0)} more Custom video${(SURPRISE_ME_UNLOCK_AT - (brainMarketingEvents ?? 0)) === 1 ? "" : "s"} so XEA learns your taste — then Surprise Me will use your usual picks.`
                      : "AI picks trending edit style & music for you, biased toward your usual taste"}
                  </p>
                  {!SURPRISE_ME_ENABLED && (
                    <div className="mt-2 h-1.5 rounded-full bg-ink-100 overflow-hidden" data-testid="surprise-me-unlock-progress">
                      <div
                        className="h-full bg-gradient-to-r from-violet-500 to-pink-500 transition-all duration-500"
                        style={{ width: `${Math.min(100, ((brainMarketingEvents ?? 0) / SURPRISE_ME_UNLOCK_AT) * 100)}%` }}
                      />
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card 
            className={`cursor-pointer transition-all ${
              mode === "custom" 
                ? "bg-gradient-to-br from-emerald-900/50 to-teal-900/50 border-coral-500/40 ring-2 ring-ink-950/30" 
                : "bg-white/60 border-ink-200 hover:border-coral-500/30"
            }`}
            onClick={() => setMode("custom")}
            data-testid="custom-mode"
          >
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center ${
                  mode === "custom" ? "bg-coral-500/30" : "bg-ink-100"
                }`}>
                  <PenTool className="w-7 h-7 text-coral-500" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-ink-950">Custom</h3>
                  <p className="text-sm text-ink-500">
                    Write your own prompt & select music
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Generation Form */}
      {canGenerate && (
        <Card className="bg-white/60 border-ink-200">
          <CardHeader>
            <CardTitle className="text-ink-950 flex items-center gap-2">
              {mode === "surprise" ? (
                <>
                  <Sparkles className="w-5 h-5 text-coral-500" />
                  Surprise Me Generation
                </>
              ) : (
                <>
                  <Wand2 className="w-5 h-5 text-coral-500" />
                  Custom Generation
                </>
              )}
            </CardTitle>
            <CardDescription>
              {mode === "surprise" 
                ? "AI will automatically select the best trending edit style and music genre based on your product"
                : "Full control over your video - write your prompt and customize every detail"
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Product Selection */}
            <div className="space-y-2">
              <Label className="text-ink-950">Select Product</Label>
              <Select 
                value={selectedProduct?.id || ""} 
                onValueChange={(id) => setSelectedProduct(products.find(p => p.id === id))}
              >
                <SelectTrigger className="bg-ink-100 border-ink-200 text-ink-950">
                  <SelectValue placeholder="Choose a product..." />
                </SelectTrigger>
                <SelectContent>
                  {products.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      <div className="flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        {product.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {products.length === 0 && (
                <p className="text-sm text-amber-400">
                  No products available — use the Custom tab to generate from a text prompt.
                </p>
              )}
            </div>

            {/* Selected Product Preview */}
            {selectedProduct && (
              <div className="p-4 rounded-xl bg-ink-50 border border-ink-200">
                <div className="flex items-center gap-4">
                  {selectedProduct.image_url ? (
                    <img 
                      src={selectedProduct.image_url} 
                      alt={selectedProduct.name}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-ink-200 flex items-center justify-center">
                      <Package className="w-6 h-6 text-ink-500" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium text-ink-950">{selectedProduct.name}</h4>
                    <p className="text-sm text-ink-500">{selectedProduct.category || "Uncategorized"}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Custom Mode Options */}
            {mode === "custom" && (
              <>
                {/* Reference image drop zone — feeds DeepSeek (via vision)
                    AND becomes the source image for Kling on Generate */}
                <div className="space-y-2" data-testid="reference-image-section">
                  <Label className="text-ink-950 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-coral-500" />
                    Reference image
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-500">optional</span>
                  </Label>
                  {referenceImage ? (
                    <div
                      data-testid="reference-image-preview"
                      className="flex items-start gap-4 p-4 rounded-2xl bg-white border border-ink-200/70"
                    >
                      <img
                        src={referenceImage.image_url}
                        alt="reference"
                        className="w-24 h-24 object-cover rounded-xl border border-ink-200/70 shrink-0"
                        onError={() => {
                          // The uploaded reference file is gone (usually means
                          // the previous container was redeployed and wiped
                          // the disk before object storage took over). Clear
                          // it so the user re-uploads instead of submitting a
                          // dead URL to Kling.
                          toast.error(
                            "Reference image is no longer available — please re-upload it.",
                          );
                          setReferenceImage(null);
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-coral-500">
                          Attached
                        </p>
                        <p className="text-sm text-ink-950 font-medium mt-1 line-clamp-3">
                          {referenceImage.description || "(no description available)"}
                        </p>
                        <p className="text-[10px] text-ink-500 mt-1">
                          XEA Assistant can now see this image and will use it for video generation.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setReferenceImage(null)}
                        data-testid="reference-image-remove"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-ink-100 hover:bg-coral-500 hover:text-white text-ink-500 transition-colors shrink-0"
                        aria-label="Remove reference"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : (
                    <label
                      htmlFor="reference-image-input"
                      data-testid="reference-image-dropzone"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer?.files?.[0];
                        if (f) handleReferenceUpload(f);
                      }}
                      className="group flex flex-col items-center justify-center gap-2 p-6 rounded-2xl border-2 border-dashed border-ink-200 hover:border-coral-500 hover:bg-coral-500/5 cursor-pointer transition-all"
                    >
                      <Upload className="w-6 h-6 text-ink-400 group-hover:text-coral-500 transition-colors" />
                      <p className="text-sm font-medium text-ink-950">
                        {uploadingRef ? "Reading the image…" : "Drop an image here, or click to upload"}
                      </p>
                      <p className="text-[10px] uppercase tracking-[0.18em] text-ink-500 font-bold">
                        JPEG · PNG · WebP · 10MB max
                      </p>
                      <input
                        id="reference-image-input"
                        type="file"
                        accept="image/*,.heic,.heif,.avif"
                        className="hidden"
                        data-testid="reference-image-input"
                        onChange={(e) => handleReferenceUpload(e.target.files?.[0])}
                        disabled={uploadingRef}
                      />
                    </label>
                  )}
                </div>

                {/* Inline XEA Assistant — context-aware brainstorming */}
                <div
                  className="relative rounded-3xl bg-gradient-to-br from-ink-50 via-white to-coral-500/5 border border-ink-200/70 p-1"
                  data-testid="custom-assistant-wrap"
                >
                  <div className="rounded-[1.35rem] bg-white">
                    <div className="px-5 pt-4 pb-2 flex items-center gap-3">
                      <span className="inline-flex items-center justify-center w-9 h-9 rounded-xl bg-ink-950">
                        <Sparkles className="w-4 h-4 text-citrus-500" />
                      </span>
                      <div className="flex-1">
                        <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-coral-500">
                          XEA Assistant · free
                        </p>
                        <p className="text-sm font-display font-bold text-ink-950">
                          Brainstorm your prompt — I know your products & past videos
                        </p>
                      </div>
                    </div>
                    <div className="px-2 pb-2">
                      <AssistantChat
                        variant="embed"
                        contextHint={
                          referenceImage
                            ? "Ask about this image, request a video prompt, get opinions…"
                            : "Describe the product or vibe, I'll draft a video prompt for you…"
                        }
                        referenceImage={referenceImage}
                        onUsePrompt={(text) => {
                          // Try to detect the structured prompt marker the chat
                          // emits ("📹 **VIDEO PROMPT** · Recommended: 20s\n\n<body>").
                          // When present, auto-pick the duration and lock the
                          // others. When absent (legacy / general suggestion),
                          // just paste the text as-is.
                          const m = text.match(
                            /📹\s*\*?\*?VIDEO PROMPT\*?\*?\s*·\s*Recommended:\s*(\d+)\s*s\s*\n+([\s\S]+?)(?:\n+✨|\n\n[A-Z]|$)/i,
                          );
                          // Pass 3 — also auto-extract the negative prompt
                          // line ("🚫 *Negative*: no morphing faces, …").
                          const neg = text.match(
                            /🚫\s*\*?\*?Negative\*?\*?\s*:\s*([^\n]+)/i,
                          );
                          if (neg && neg[1]) {
                            setNegativePrompt(neg[1].trim().slice(0, 500));
                          }
                          if (m) {
                            const recDur = m[1];
                            const promptBody = m[2].trim();
                            // Pass 4 — accept any duration 5-60 the AssistantChat suggests.
                            const recDurNum = parseInt(recDur, 10);
                            if (Number.isFinite(recDurNum) && recDurNum >= 5 && recDurNum <= 60) {
                              setDuration(String(recDurNum));
                              setAiSuggestedDuration(String(recDurNum));
                            }
                            setCustomPrompt(promptBody.slice(0, 2000));
                            toast.success(
                              `Prompt applied · AI locked the duration to ${recDur}s${neg ? " + negative prompt filled" : ""}`,
                            );
                          } else {
                            setCustomPrompt(text.slice(0, 2000));
                            setAiSuggestedDuration(null);
                            toast.success("Prompt copied into the editor below");
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-ink-950">Video Prompt</Label>
                    {multiShotAvailable && (
                      <label className="flex items-center gap-2 text-xs text-ink-600 cursor-pointer" data-testid="multi-shot-toggle-wrap">
                        <span>Multi-shot</span>
                        <Switch
                          checked={multiShot}
                          onCheckedChange={setMultiShot}
                          data-testid="multi-shot-toggle"
                        />
                      </label>
                    )}
                  </div>
                  {multiShot && multiShotAvailable ? (
                    <div className="space-y-3">
                      <p className="text-[11px] text-ink-500">
                        Multi-shot splits your {resolvedDurationInt}s video into 2 clips with a distinct prompt each. Tip: write the wide shot first, then the close-up.
                      </p>
                      {[0, 1].map((idx) => (
                        <div key={idx} className="space-y-1">
                          <Label className="text-[10px] uppercase tracking-wider text-ink-500">
                            Shot {idx + 1}
                          </Label>
                          <Textarea
                            value={shotPrompts[idx] || ""}
                            onChange={(e) => {
                              const next = [...shotPrompts];
                              next[idx] = e.target.value.slice(0, 1000);
                              setShotPrompts(next);
                            }}
                            placeholder={idx === 0
                              ? "Beat 1 — e.g. wide cinematic shot of the product on marble at golden hour, slow dolly-in"
                              : "Beat 2 — e.g. close-up of the label with subtle rack-focus and rim light"}
                            className="bg-ink-100 border-ink-200 text-ink-950 min-h-[70px]"
                            maxLength={1000}
                            data-testid={`shot-${idx + 1}-input`}
                          />
                          <p className="text-[10px] text-ink-500 text-right">{(shotPrompts[idx] || "").length}/1000</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <>
                      <Textarea
                        value={customPrompt}
                        onChange={(e) => setCustomPrompt(e.target.value.slice(0, 2000))}
                        placeholder="Describe how you want your product video to look... e.g., 'Dramatic zoom into product with sparkle effects, luxury feel'"
                        className="bg-ink-100 border-ink-200 text-ink-950 min-h-[100px]"
                        maxLength={2000}
                        data-testid="custom-prompt-input"
                      />
                      <p className="text-[10px] text-ink-500 mt-1 text-right" data-testid="custom-prompt-counter">{customPrompt.length}/2000</p>
                    </>
                  )}
                </div>

                {/* ── Pass 3 · Advanced (negative prompt + SFX) ───────── */}
                <div className="border border-ink-200 rounded-xl bg-ink-50/50">
                  <button
                    type="button"
                    onClick={() => setShowAdvanced((v) => !v)}
                    className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-ink-950 hover:bg-ink-100/50 rounded-xl transition-colors"
                    data-testid="advanced-toggle"
                  >
                    <span className="flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-violet-500" />
                      Advanced — negative prompt &amp; SFX
                      {(negativePrompt || sfxChosen) && (
                        <span className="text-[10px] px-2 py-0.5 bg-violet-100 text-violet-700 rounded-full">
                          {[negativePrompt && "neg", sfxChosen && "sfx"].filter(Boolean).join(" · ")}
                        </span>
                      )}
                    </span>
                    <ChevronRight className={`w-4 h-4 transition-transform ${showAdvanced ? "rotate-90" : ""}`} />
                  </button>

                  {showAdvanced && (
                    <div className="px-4 pb-4 space-y-4 border-t border-ink-200">
                      {/* Negative prompt */}
                      <div className="space-y-2 pt-3">
                        <Label className="text-ink-950 text-xs">
                          🚫 Negative prompt — what to AVOID
                        </Label>
                        <Textarea
                          value={negativePrompt}
                          onChange={(e) => setNegativePrompt(e.target.value.slice(0, 500))}
                          placeholder="e.g. no morphing faces, no warped limbs, no distortion, no extra fingers, no blurry text"
                          className="bg-ink-100 border-ink-200 text-ink-950 min-h-[60px]"
                          maxLength={500}
                          data-testid="negative-prompt-input"
                        />
                        <p className="text-[10px] text-ink-500 text-right">{negativePrompt.length}/500 · The AssistantChat above auto-fills this for you</p>
                      </div>

                      {/* SFX picker */}
                      <div className="space-y-2">
                        <Label className="text-ink-950 text-xs">
                          🔊 SFX overlay — short sound effect at 0:00
                        </Label>
                        <div className="flex flex-wrap gap-1.5">
                          {["epic whoosh", "deep cinematic impact", "magical sparkle shimmer", "swipe transition", "subtle pop", "vinyl crackle warm"].map((preset) => (
                            <button
                              key={preset}
                              type="button"
                              onClick={() => setSfxPrompt(preset)}
                              className={`text-[11px] px-2.5 py-1 rounded-full border transition ${
                                sfxPrompt === preset
                                  ? "bg-violet-500 text-white border-violet-500"
                                  : "bg-white border-ink-200 text-ink-700 hover:border-violet-300"
                              }`}
                              data-testid={`sfx-preset-${preset.split(" ")[0]}`}
                            >
                              {preset}
                            </button>
                          ))}
                        </div>
                        <div className="flex gap-2">
                          <Input
                            value={sfxPrompt}
                            onChange={(e) => setSfxPrompt(e.target.value.slice(0, 200))}
                            placeholder="Or type any SFX — e.g. 'glass shatter reverb'"
                            className="bg-ink-100 border-ink-200 text-ink-950 text-sm flex-1"
                            data-testid="sfx-prompt-input"
                          />
                          <Select
                            value={String(sfxDuration)}
                            onValueChange={(v) => setSfxDuration(Number(v))}
                          >
                            <SelectTrigger className="bg-ink-100 border-ink-200 w-[100px]" data-testid="sfx-duration-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1s</SelectItem>
                              <SelectItem value="2">2s</SelectItem>
                              <SelectItem value="3">3s</SelectItem>
                              <SelectItem value="5">5s</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            type="button"
                            disabled={!sfxPrompt.trim() || generatingSfx}
                            onClick={async () => {
                              setGeneratingSfx(true);
                              try {
                                const r = await axios.post(
                                  `${API_URL}/api/music/elevenlabs/sfx`,
                                  { prompt: sfxPrompt.trim(), duration_seconds: sfxDuration },
                                  { headers: { Authorization: `Bearer ${token}` } },
                                );
                                setSfxChosen(r.data);
                                toast.success("SFX ready · will mix at 0:00 of your video");
                              } catch (e) {
                                toast.error(errMsg(e, "SFX generation failed"));
                              } finally {
                                setGeneratingSfx(false);
                              }
                            }}
                            data-testid="sfx-generate-btn"
                            className="bg-violet-500 hover:bg-violet-600 text-white"
                          >
                            {generatingSfx ? <Loader2 className="w-4 h-4 animate-spin" /> : "Generate"}
                          </Button>
                        </div>
                        {sfxChosen && (
                          <div className="flex items-center gap-2 p-2 bg-violet-50 border border-violet-200 rounded-lg" data-testid="sfx-chosen">
                            <CheckCircle2 className="w-4 h-4 text-violet-600 shrink-0" />
                            <span className="text-xs text-violet-900 flex-1 truncate">&ldquo;{sfxChosen.prompt}&rdquo; · {sfxChosen.duration}s</span>
                            <audio src={`${API_URL}${sfxChosen.sfx_url}`} controls className="h-7 max-w-[160px]" />
                            <button
                              type="button"
                              onClick={() => setSfxChosen(null)}
                              className="text-violet-700 hover:text-violet-900"
                              data-testid="sfx-clear-btn"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        )}
                        <p className="text-[10px] text-ink-500">Pro+ feature · Powered by ElevenLabs Sound Effects</p>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-ink-950">Video Style</Label>
                    <Select value={videoStyle} onValueChange={setVideoStyle}>
                      <SelectTrigger className="bg-ink-100 border-ink-200 text-ink-950">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trendy">Trendy (TikTok Style)</SelectItem>
                        <SelectItem value="professional">Professional</SelectItem>
                        <SelectItem value="cinematic">Cinematic</SelectItem>
                        <SelectItem value="dynamic">Dynamic</SelectItem>
                        <SelectItem value="minimal">Minimal</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-3 md:col-span-2">
                    {/* ── Pass 4 polish-mode chooser ─────────────────── */}
                    <Label className="text-ink-950 flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-pink-400" />
                      How should we finish your video?
                    </Label>
                    <div className="grid grid-cols-3 gap-2" data-testid="polish-mode-picker">
                      {[
                        { id: "music",      label: "🎵 With song",        sub: "AI picks the best engine for the vibe you describe" },
                        { id: "no_music",   label: "🔇 No music",         sub: "Polished video without any soundtrack — keeps Kling's native audio" },
                        { id: "brand_only", label: "🎤 Brand only",       sub: "Brand frame + CTA · keep Kling's native audio · NO music" },
                      ].map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => setPolishMode(m.id)}
                          data-testid={`polish-mode-${m.id}`}
                          aria-pressed={polishMode === m.id}
                          className={`p-3 rounded-xl border text-left transition ${
                            polishMode === m.id
                              ? "border-pink-500 bg-pink-50 ring-2 ring-pink-300"
                              : "border-ink-200 bg-white hover:border-ink-300"
                          }`}
                        >
                          <div className="text-sm font-semibold text-ink-950">{m.label}</div>
                          <div className="text-[10px] text-ink-500 mt-1 leading-tight">{m.sub}</div>
                        </button>
                      ))}
                    </div>

                    {/* Genre input — only relevant for "music" mode */}
                    {polishMode === "music" && (
                      <div className="space-y-1.5">
                        <Label className="text-[11px] uppercase tracking-wider text-ink-500">
                          🎼 Genre / vibe — what should the song feel like?
                        </Label>
                        <Input
                          value={musicGenre}
                          onChange={(e) => setMusicGenre(e.target.value.slice(0, 120))}
                          placeholder="e.g. 'cinematic synthwave', 'lo-fi study beats', 'epic orchestral'"
                          className="bg-ink-100 border-ink-200 text-ink-950 text-sm"
                          maxLength={120}
                          data-testid="music-genre-input"
                        />
                        <p className="text-[10px] text-ink-500">
                          Leave blank to let the AI auto-pick. The AssistantChat above can suggest one — it&apos;ll ask you.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* "Music for your video" picker removed — when polishMode === 'music',
                      the backend auto-picks via the Smart Music dialog below or AI vibe. */}
                </div>
              </>
            )}

            {/* Common Options */}
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-ink-950 flex items-center gap-2">
                  Duration
                </Label>
                {/* Duration is system-driven: the AssistantChat suggests one
                    with each prompt, and we use that (or default to 15s).
                    The manual "Customize" override was removed per founder
                    spec — system-only keeps the surface focused. */}
                <div
                  className="p-3 rounded-xl border border-ink-200 bg-ink-50/50"
                  data-testid="duration-auto-display"
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-base font-bold text-ink-950">
                      {aiSuggestedDuration ? `${aiSuggestedDuration}s` : "15s (default)"}
                    </span>
                    <span className="text-[10px] font-bold uppercase tracking-[0.10em] text-citrus-700">
                      {aiSuggestedDuration ? "✨ AI picked" : "🤖 System"}
                    </span>
                  </div>
                  <p className="text-[10px] text-ink-500 mt-1 leading-tight">
                    The AssistantChat above sets the duration based on your prompt — the system handles the rest.
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-ink-950">Aspect Ratio</Label>
                <Select value={aspectRatio} onValueChange={setAspectRatio}>
                  <SelectTrigger className="bg-ink-100 border-ink-200 text-ink-950">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="9:16">Portrait (9:16) - TikTok/Reels</SelectItem>
                    <SelectItem value="16:9">Landscape (16:9) - YouTube</SelectItem>
                    <SelectItem value="1:1">Square (1:1) - Instagram</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Kling Model Picker */}
            <div className="space-y-2">
              <Label className="text-ink-950">AI Model</Label>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  {
                    id: "kling-v2-6",
                    name: "Kling v2.6",
                    tagline: "Reliable workhorse",
                    description:
                      "Battle-tested for product shots. Fast renders, native audio, supports 15s & 20s (stitched).",
                    badge: "DEFAULT",
                  },
                  {
                    id: "kling-v3",
                    name: "Kling v3",
                    tagline: "Premium quality",
                    description:
                      "Sharper motion, richer details, more cinematic. Required for native 15s clips and 30s stitches.",
                    badge: "PREMIUM",
                  },
                ].map((opt) => {
                  const active = klingModel === opt.id;
                  return (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => setKlingModel(opt.id)}
                      data-testid={`model-${opt.id}-btn`}
                      aria-pressed={active}
                      className={`text-left p-4 rounded-2xl border transition-all duration-200 ${
                        active
                          ? "bg-ink-950 text-white border-ink-950 shadow-[0_8px_24px_rgba(10,10,10,0.18)]"
                          : "bg-white border-ink-200 text-ink-950 hover:border-ink-400 hover:-translate-y-0.5"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-display font-bold text-base tracking-tight">
                          {opt.name}
                        </p>
                        <span
                          className={`text-[9px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full ${
                            active
                              ? "bg-citrus-500 text-ink-950"
                              : opt.badge === "PREMIUM"
                              ? "bg-coral-500/15 text-coral-600"
                              : "bg-ink-100 text-ink-500"
                          }`}
                        >
                          {opt.badge}
                        </span>
                      </div>
                      <p
                        className={`text-xs mt-1 font-semibold ${
                          active ? "text-citrus-500" : "text-coral-500"
                        }`}
                      >
                        {opt.tagline}
                      </p>
                      <p
                        className={`text-xs mt-2 leading-relaxed font-light ${
                          active ? "text-white/70" : "text-ink-500"
                        }`}
                      >
                        {opt.description}
                      </p>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Audio Toggle */}
            <div className="flex items-center justify-between p-4 rounded-xl bg-ink-50 border border-ink-200">
              <div>
                <p className="text-ink-950 text-sm font-medium">Generate with Audio</p>
                <p className="text-xs text-ink-500 mt-0.5">
                  {enableAudio ? "AI will generate native audio, sound effects & voice" : "Silent video — add your own music/voiceover later"}
                </p>
              </div>
              <button
                onClick={() => setEnableAudio(!enableAudio)}
                className={`relative w-12 h-6 rounded-full transition-colors ${enableAudio ? "bg-coral-500" : "bg-ink-300"}`}
                data-testid="audio-toggle"
              >
                <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${enableAudio ? "translate-x-6" : "translate-x-0"}`} />
              </button>
            </div>

            {/* Generate Button */}
            <Button
              onClick={mode === "surprise" ? handleSurpriseMe : handleCustomGenerate}
              disabled={
                loading ||
                (mode === "surprise"
                  ? !selectedProduct
                  : !selectedProduct && !referenceImage)
              }
              className={`w-full h-12 text-lg ${
                mode === "surprise"
                  ? "bg-coral-500 hover:bg-coral-600"
                  : "bg-ink-950 hover:bg-ink-900"
              }`}
              data-testid="generate-video-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  {mode === "surprise" ? (
                    <>
                      <Shuffle className="w-5 h-5 mr-2" />
                      Surprise Me!
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5 mr-2" />
                      Generate Video
                    </>
                  )}
                </>
              )}
            </Button>

            <p className="text-xs text-ink-500 text-center">
              {duration === "15" ? "10" : "20"} tokens • {duration === "15" ? "~1–2 min" : "~2–4 min (2 clips stitched)"}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Video History */}
      <Card className="bg-white/60 border-ink-200">
        <CardHeader>
          <CardTitle className="text-ink-950 flex items-center gap-2">
            <Clock className="w-5 h-5 text-ink-500" />
            Generated Videos
          </CardTitle>
        </CardHeader>
        <CardContent>
          {videoHistory.length === 0 ? (
            <div className="text-center py-12 text-ink-500">
              <Video className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <p>No videos generated yet</p>
              <p className="text-sm mt-1">Your generated videos will appear here</p>
            </div>
          ) : (
            <div className="space-y-4">
              {videoHistory.map((video) => (
                <div 
                  key={video.id}
                  className="p-4 rounded-xl bg-ink-50 border border-ink-200"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-4 flex-1">
                      {/* Video Preview or Placeholder */}
                      <div className="w-24 h-24 rounded-lg bg-ink-200 flex-shrink-0 overflow-hidden">
                        {displayUrl(video) && !isPolishing(video) ? (
                          <video 
                            src={displayUrl(video)}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            {(video.status === "processing" || isPolishing(video)) ? (
                              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
                            ) : (
                              <Video className="w-8 h-8 text-ink-500" />
                            )}
                          </div>
                        )}
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          {getStatusIcon(video.status)}
                          <span className={`text-sm font-medium capitalize ${getStatusColor(video.status)}`}>
                            {video.status}
                          </span>
                          {video.edit_style && (
                            <span className="px-2 py-0.5 text-xs bg-coral-500/20 text-violet-300 rounded-full">
                              {video.edit_style}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-ink-700 line-clamp-2">
                          {video.user_prompt || video.prompt?.slice(0, 100)}...
                        </p>
                          {video.music_suggestion && (
                            <p className="text-xs text-pink-400 mt-1 flex items-center gap-1">
                              <Music className="w-3 h-3" />
                              {video.music_suggestion}
                            </p>
                          )}
                          {video.status === "processing" && (
                            <p className="text-[11px] text-amber-500 mt-1 font-mono" data-testid="processing-msg">
                              ⏱ {processingMsg(video)}
                            </p>
                          )}
                          <p className="text-xs text-ink-500 mt-1">
                            {new Date(video.created_at).toLocaleString()}
                          </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex flex-col gap-2">
                      {displayUrl(video) && !isPolishing(video) && (
                        <>
                          <Button
                            size="sm"
                            className="w-full bg-ink-950 hover:bg-ink-900"
                            onClick={() => setPlayerVideo({
                              url: displayUrl(video),
                              title: video.user_prompt || video.prompt?.slice(0, 80) || "Video",
                            })}
                            data-testid={`video-play-btn-${video.id}`}
                          >
                            <Play className="w-4 h-4 mr-1" />
                            Play
                          </Button>
                          <a
                            href={displayUrl(video)}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            onClick={(e) => {
                              // On iOS, intercept and route through Web Share API
                              // for the native "Save to Files / Drive" sheet.
                              if (/iPad|iPhone|iPod/.test(navigator.userAgent || "")) {
                                e.preventDefault();
                                iosSave(displayUrl(video), { kind: "video", filename: `xea-video-${video.id}.mp4` });
                              }
                            }}
                            data-testid={`video-download-btn-${video.id}`}
                          >
                            <Button size="sm" variant="outline" className="w-full border-ink-300">
                              <Download className="w-4 h-4 mr-1" />
                              Download
                            </Button>
                          </a>
                          {/* One-tap social share */}
                          <SocialShareBar
                            mediaUrl={displayUrl(video)}
                            caption={video.prompt}
                            variant="compact"
                            className="mt-1"
                          />
                          {/* Share to in-app community */}
                          <ShareToCommunityButton
                            sourceCollection="video_tasks"
                            sourceId={video.id}
                            size="sm"
                            variant="outline"
                            className="w-full border-coral-500/40 text-coral-600 hover:bg-coral-500/10"
                            testId={`video-share-to-community-${video.id}`}
                          />
                        </>
                      )}
                      {isPolishing(video) && (
                        <div
                          data-testid={`auto-polishing-${video.id}`}
                          className="text-xs text-amber-500 font-medium px-2 py-1.5 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center gap-1.5"
                        >
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          Polishing…
                        </div>
                      )}
                      {video.status === "completed" && (
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="border-ink-300"
                          onClick={() => {
                            setEditingTaskId(video.id);
                            setEditPrompt("");
                            // Default edit duration to the original video's length when supported
                            const orig = String(video.duration || 15);
                            setEditDuration(["15", "20", "30"].includes(orig) ? orig : "15");
                          }}
                        >
                          <RefreshCw className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Polish for socials — adds intro, outro, music & text overlays */}
                  {video.status === "completed" && video.video_url && (
                    <>
                      {/* Auto-suggest polish for longer videos (20s+) — they almost always benefit */}
                      {video.duration >= 20 && !video.polished_url && !video.polish_status && (
                        <div className="mt-3 px-3 py-2 rounded-xl bg-citrus-500/10 border border-citrus-500/40 flex items-center gap-2" data-testid={`polish-suggested-${video.id}`}>
                          <Sparkles className="w-3.5 h-3.5 text-ink-950 flex-shrink-0" />
                          <p className="text-xs text-ink-950 font-medium">
                            <span className="font-bold">Polish recommended</span> — longer videos shine with music, captions & a CTA.
                          </p>
                        </div>
                      )}
                      <PolishVideoButton video={video} onPolished={fetchVideoHistory} />
                    </>
                  )}

                  {/* Edit Section */}
                  {editingTaskId === video.id && (
                    <div className="mt-4 pt-4 border-t border-ink-200 space-y-3" data-testid={`edit-panel-${video.id}`}>
                      <Label className="text-ink-950 text-sm">Edit Instructions</Label>
                      <Textarea
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        placeholder="Describe what you want to change... e.g., 'Make it more dramatic' or 'Add slower zoom'"
                        className="bg-ink-200 border-ink-300 text-ink-950"
                      />
                      <div className="space-y-1.5">
                        <Label className="text-ink-950 text-xs uppercase tracking-[0.18em]">Edit duration</Label>
                        <div className="grid grid-cols-3 gap-2" data-testid={`edit-duration-picker-${video.id}`}>
                          {[
                            { value: "15", label: "15s", tokens: 10 },
                            { value: "20", label: "20s", tokens: 20 },
                            { value: "30", label: "30s", tokens: 20 },
                          ].map((opt) => {
                            const active = editDuration === opt.value;
                            return (
                              <button
                                key={opt.value}
                                type="button"
                                onClick={() => setEditDuration(opt.value)}
                                data-testid={`edit-duration-${opt.value}-${video.id}`}
                                aria-pressed={active}
                                className={`text-left px-3 py-2 rounded-xl border transition-all ${
                                  active
                                    ? "bg-ink-950 text-white border-ink-950"
                                    : "bg-white border-ink-200 text-ink-950 hover:border-ink-400"
                                }`}
                              >
                                <div className="flex items-baseline justify-between gap-2">
                                  <span className="text-sm font-bold">{opt.label}</span>
                                  <span className={`text-[10px] font-bold uppercase tracking-[0.10em] ${active ? "text-citrus-500" : "text-ink-500"}`}>
                                    {opt.tokens}
                                  </span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleEditVideo(video.id)}
                          disabled={loading}
                          className="bg-coral-500 hover:bg-coral-600"
                          data-testid={`edit-submit-${video.id}`}
                        >
                          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply Edit"}
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingTaskId(null)}
                          className="border-ink-300"
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* Smart Music — unified entry point that routes to Jamendo / Mubert*/}
      {/* / ElevenLabs based on what the user types and their tier.        */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Dialog open={smartMusicOpen} onOpenChange={setSmartMusicOpen}>
        <DialogContent className="max-w-2xl" data-testid="smart-music-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">🔍 Find or generate music</DialogTitle>
            <DialogDescription>
              Type a song you like, pick a vibe, or let AI generate something fresh — all in one place.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                placeholder='e.g. "Blinding Lights" or "chill lofi for studying"'
                value={jamendoQuery}
                onChange={(e) => setJamendoQuery(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleJamendoSearch(); } }}
                data-testid="smart-music-input"
              />
              <Button
                onClick={handleJamendoSearch}
                disabled={jamendoLoading || !jamendoQuery.trim()}
                data-testid="smart-music-search-btn"
              >
                {jamendoLoading ? "Searching…" : "Search"}
              </Button>
            </div>

            {/* Quick vibe chips — one tap = instant Mubert AI instrumental */}
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-ink-500">
                Or pick a vibe (instant AI instrumental)
              </p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { vibe: "trendy",    label: "✨ Upbeat" },
                  { vibe: "energetic", label: "💪 Workout" },
                  { vibe: "chill",     label: "☕ Chill" },
                  { vibe: "luxury",    label: "🌃 Cinematic" },
                  { vibe: "tiktok",    label: "💃 Dance" },
                  { vibe: "ambient",   label: "🧘 Ambient" },
                ].map((c) => (
                  <button
                    key={c.vibe}
                    type="button"
                    disabled={generatingAI}
                    onClick={async () => {
                      // Reuse handleGenerateAIMusic by setting the vibe temporarily
                      // via musicPreference (the function already pipes through).
                      setMusicPreference(c.vibe);
                      await handleGenerateAIMusic();
                      setSmartMusicOpen(false);
                    }}
                    className="px-2.5 py-1 rounded-full text-xs font-bold border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100 disabled:opacity-50 disabled:cursor-wait"
                    data-testid={`smart-vibe-${c.vibe}`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Inline Jamendo results when search returns hits */}
            {jamendoTracks.length > 0 && (
              <div className="space-y-1.5">
                <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-emerald-700">
                  🎵 Real songs (Jamendo · commercial-safe)
                </p>
                <div className="grid gap-1.5 max-h-[280px] overflow-y-auto">
                  {jamendoTracks.slice(0, 8).map((t) => (
                    <div
                      key={t.id}
                      className="flex items-center gap-2 p-2 rounded-lg border border-ink-200 hover:border-emerald-400 transition"
                    >
                      {t.image && <img src={t.image} alt="" className="w-9 h-9 rounded-md object-cover flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold text-ink-950 truncate">{t.name}</div>
                        <div className="text-[10px] text-ink-500 truncate">
                          {t.artist_name} · {Math.floor((t.duration || 0) / 60)}:{String((t.duration || 0) % 60).padStart(2, "0")}
                        </div>
                      </div>
                      <audio controls src={t.preview_url} preload="none" className="h-7" />
                      <Button
                        size="sm"
                        onClick={async () => {
                          await handleJamendoSelect(t);
                          setSmartMusicOpen(false);
                        }}
                        disabled={jamendoSelecting === t.id}
                        data-testid={`smart-jamendo-pick-${t.id}`}
                      >
                        {jamendoSelecting === t.id ? "…" : "Use"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* AI generation fallback — vibe-translated ElevenLabs for Pro+,
                otherwise a hint pointing free users at Jamendo / vibe chips. */}
            <div className="border-t border-ink-200 pt-3">
              {(() => {
                const tier = (user?.subscription_tier || "free").toLowerCase();
                const elevenUnlocked = ["pro", "platinum", "ultimate"].includes(tier);
                return elevenUnlocked ? (
                  <div className="space-y-1.5">
                    <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-fuchsia-700">
                      🎤 Or generate an original vocal song (Pro+)
                    </p>
                    <p className="text-[11px] text-ink-500">
                      Type what you like above, then tap this to open the AI vocal song studio (with the &ldquo;Capture vibe&rdquo; translator).
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setVibeRef(jamendoQuery);
                        setSmartMusicOpen(false);
                        setElevenOpen(true);
                      }}
                      data-testid="smart-music-open-eleven"
                    >
                      🎤 Generate AI vocal song →
                    </Button>
                  </div>
                ) : (
                  <p className="text-[11px] text-ink-500">
                    🎤 AI vocal songs (Pro+) — upgrade to unlock. Free / Starter / Premium can use Jamendo search + AI instrumental vibes above.
                  </p>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* Jamendo browse dialog — real human-made songs (commercial-safe)  */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Dialog open={jamendoOpen} onOpenChange={setJamendoOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="jamendo-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🎵 Browse real songs
            </DialogTitle>
            <DialogDescription>
              600K+ human-made tracks from Jamendo. Filtered to CC-BY / CC-BY-SA only — safe for your commercial videos. Attribution recommended.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col sm:flex-row gap-2">
            <Input
              placeholder="Search by mood, vibe, or keyword… e.g. 'upbeat workout'"
              value={jamendoQuery}
              onChange={(e) => setJamendoQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleJamendoSearch(); }}
              data-testid="jamendo-search-input"
            />
            <Select value={jamendoMood} onValueChange={setJamendoMood}>
              <SelectTrigger className="sm:w-[180px]" data-testid="jamendo-mood-select">
                <SelectValue placeholder="Mood (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="happy">Happy</SelectItem>
                <SelectItem value="energetic">Energetic</SelectItem>
                <SelectItem value="chill">Chill</SelectItem>
                <SelectItem value="epic">Epic</SelectItem>
                <SelectItem value="romantic">Romantic</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="uplifting">Uplifting</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={handleJamendoSearch} disabled={jamendoLoading} data-testid="jamendo-search-btn">
              {jamendoLoading ? "Searching…" : "Search"}
            </Button>
          </div>

          <div className="flex-1 overflow-y-auto -mx-2 px-2 mt-3">
            {jamendoTracks.length === 0 && !jamendoLoading && (
              <div className="text-center text-sm text-ink-500 py-12">
                Search above to browse Jamendo&apos;s catalog.
              </div>
            )}
            <div className="grid gap-2">
              {jamendoTracks.map((t) => (
                <div
                  key={t.id}
                  className="flex items-center gap-3 p-2 rounded-lg border border-ink-200 hover:border-emerald-400 transition"
                  data-testid={`jamendo-track-${t.id}`}
                >
                  {t.image && (
                    <img src={t.image} alt="" className="w-12 h-12 rounded-md object-cover flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-ink-950 truncate">{t.name}</div>
                    <div className="text-xs text-ink-500 truncate">
                      {t.artist_name} · {Math.floor((t.duration || 0) / 60)}:{String((t.duration || 0) % 60).padStart(2, "0")}
                      {t.bpm ? ` · ${t.bpm} BPM` : ""}
                    </div>
                  </div>
                  <audio controls src={t.preview_url} preload="none" className="h-8" />
                  <Button
                    size="sm"
                    onClick={() => handleJamendoSelect(t)}
                    disabled={jamendoSelecting === t.id}
                    data-testid={`jamendo-pick-${t.id}`}
                  >
                    {jamendoSelecting === t.id ? "Attaching…" : "Use this"}
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <p className="text-[10px] text-ink-400 mt-2">
            Tracks are licensed under Creative Commons — credit the artist + Jamendo in your video description when feasible.
          </p>
        </DialogContent>
      </Dialog>

      {/* ──────────────────────────────────────────────────────────────── */}
      {/* ElevenLabs Music dialog — AI vocal song generation (Pro+ only)   */}
      {/* ──────────────────────────────────────────────────────────────── */}
      <Dialog open={elevenOpen} onOpenChange={setElevenOpen}>
        <DialogContent className="max-w-lg" data-testid="elevenlabs-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              🎤 Generate AI vocal song
            </DialogTitle>
            <DialogDescription>
              ElevenLabs Music creates a unique song from your prompt — vocals + lyrics + production, cleared for commercial use. Takes ~30-60 s.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Song-alike vibe translator — type a real song, let Claude
                describe its musical fingerprint into a copyright-safe prompt */}
            <div className="space-y-1 p-2.5 rounded-lg border border-dashed border-fuchsia-300 bg-fuchsia-50/40">
              <Label className="text-xs flex items-center gap-1 text-fuchsia-700">
                ✨ Type a song you like (optional)
              </Label>
              <div className="flex gap-2">
                <Input
                  value={vibeRef}
                  onChange={(e) => setVibeRef(e.target.value.slice(0, 200))}
                  placeholder='e.g. "Blinding Lights" or "Lose Yourself by Eminem"'
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleTranslateVibe(); } }}
                  data-testid="elevenlabs-vibe-ref-input"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleTranslateVibe}
                  disabled={vibeTranslating || vibeRef.trim().length < 2}
                  data-testid="elevenlabs-translate-vibe-btn"
                >
                  {vibeTranslating ? "Capturing…" : "Capture vibe"}
                </Button>
              </div>
              <p className="text-[10px] text-fuchsia-700/70">
                We never send the song name to the AI music generator (copyright). Instead, Claude describes its tempo, instruments and feel, which ElevenLabs then uses to compose something original in the same style.
              </p>
            </div>

            <div className="space-y-1">
              <Label className="text-xs">Describe the song</Label>
              <Textarea
                value={elevenPrompt}
                onChange={(e) => setElevenPrompt(e.target.value.slice(0, 400))}
                placeholder="e.g. Upbeat pop anthem about morning coffee, female lead vocal, catchy chorus, 110 BPM"
                className="min-h-[90px]"
                data-testid="elevenlabs-prompt-input"
              />
              <p className="text-[10px] text-ink-400">
                Tip: avoid mentioning real artist names or song titles — ElevenLabs blocks those.
              </p>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex-1">
                <Label className="text-xs">Duration</Label>
                <Select value={String(elevenDuration)} onValueChange={(v) => setElevenDuration(parseInt(v))}>
                  <SelectTrigger data-testid="elevenlabs-duration-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="15">15 seconds</SelectItem>
                    <SelectItem value="20">20 seconds</SelectItem>
                    <SelectItem value="30">30 seconds</SelectItem>
                    <SelectItem value="45">45 seconds</SelectItem>
                    <SelectItem value="60">60 seconds</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2 pt-5">
                <Switch
                  checked={elevenInstrumental}
                  onCheckedChange={setElevenInstrumental}
                  data-testid="elevenlabs-instrumental-toggle"
                />
                <Label className="text-xs cursor-pointer">Instrumental only</Label>
              </div>
            </div>

            {/* Pass 5 — optional lyrics, hidden when instrumental */}
            {!elevenInstrumental && (
              <div className="space-y-1.5">
                <Label className="text-xs flex items-center gap-1.5">
                  🎤 Lyrics
                  <span className="text-[10px] text-ink-400 font-normal">
                    optional · use [Verse]/[Chorus] markers for structure
                  </span>
                </Label>
                <Textarea
                  value={elevenLyrics}
                  onChange={(e) => setElevenLyrics(e.target.value.slice(0, 2000))}
                  placeholder={"[Verse 1]\nWalking through the city lights\nFollowing my dreams tonight\n\n[Chorus]\nWe're alive, we're alive\nFeel the rhythm, feel the vibe"}
                  className="bg-ink-100 border-ink-200 text-ink-950 min-h-[100px] font-mono text-xs"
                  maxLength={2000}
                  data-testid="elevenlabs-lyrics-input"
                />
                <p className="text-[10px] text-ink-400 text-right">{elevenLyrics.length}/2000</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setElevenOpen(false)} disabled={elevenGenerating}>
              Cancel
            </Button>
            <Button
              onClick={handleElevenLabsGenerate}
              disabled={elevenGenerating || elevenPrompt.trim().length < 4}
              data-testid="elevenlabs-generate-btn"
            >
              {elevenGenerating ? "Generating… (~30-60 s)" : "Generate song"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* In-app video player — opened by Play buttons */}
      <VideoPlayerDialog
        open={!!playerVideo}
        onClose={() => setPlayerVideo(null)}
        url={playerVideo?.url}
        title={playerVideo?.title}
      />
    </div>
  );
};

export default AIVideoGeneratorPage;
